import tabela_cest from './nfce/tabela_cest.json';
import tabela_cfop from './nfce/tabela_cfop.json';
import tabela_cclasstrib from './nfce/tabela_cclasstrib.json';
import { TRIBUTARY_SITUATION } from '../../types/fiscal';

const officialCESTCodes = tabela_cest.map(item => item.CEST.replace(/\D/g, ''));
const officialCFOPCodes = tabela_cfop.map(item => item.cfop.replace(/\D/g, ''));
const officialCClassTribCodes = (tabela_cclasstrib || [])
  .map(item => String(item.code || '').replace(/\D/g, ''))
  .filter(Boolean);
const genericCEST = ['0000000'];
const validCST = ['00', '10', '20', '30', '40', '41', '50', '51', '60', '70', '90'];
const validCSOSN = ['000', '101', '102', '103', '201', '202', '203', '300', '400', '500', '900'];
const validAliqICMS = ['0', '3.2', '3.5', '4', '7', '12', '17', '18', '25'];
const validPercentualReducao = ['0', '5', '10', '15', '20', '25', '30', '40', '50', '60', '70', '80', '90', '100'];

const toOptions = values => values.map(value => ({ value, text: value }));
const normalize = (value) => String(value || '').toLowerCase().trim();

export const buildOptionsWithCurrent = (baseOptions, currentValue) => {
  if (!currentValue) return baseOptions;
  if (baseOptions.some(item => String(item.value) === String(currentValue))) return baseOptions;
  return [{ value: currentValue, text: `${currentValue} (atual)` }, ...baseOptions];
};

export const CST_OPTIONS_BASE = [{ value: '', text: 'Selecione' }, ...toOptions(validCST)];
export const CSOSN_OPTIONS_BASE = [{ value: '', text: 'Selecione' }, ...toOptions(validCSOSN)];
export const ALIQ_ICMS_OPTIONS_BASE = [
  { value: '', text: 'Selecione' },
  ...validAliqICMS.map(value => ({ value, text: value.replace('.', ',') })),
];
export const PERCENTUAL_REDUCAO_OPTIONS_BASE = [{ value: '', text: 'Selecione' }, ...toOptions(validPercentualReducao)];
export const CST_CBS_IBS_OPTIONS_BASE = [
  { value: '', text: 'Selecione' },
  { value: '00', text: '000 - Tributação integral' },
  { value: '010', text: '010 - Tributação com alíquotas uniformes' },
  { value: '011', text: '011 - Tributação com alíquotas uniformes reduzidas' },
  { value: '200', text: '200 - Alíquota reduzida' },
  { value: '210', text: '210 - Redução de alíquota com redutor de base de cálculo' },
  { value: '220', text: '220 - Alíquota fixa' },
  { value: '221', text: '221 - Alíquota fixa proporcional' },
  { value: '222', text: '222 - Redução de base de cálculo' },
  { value: '400', text: '400 - Isenção' },
  { value: '410', text: '410 - Imunidade e não incidência' },
  { value: '510', text: '510 - Diferimento' },
  { value: '515', text: '515 - Diferimento com redução de alíquota' },
  { value: '550', text: '550 - Suspensão' },
  { value: '620', text: '620 - Tributação monofásica' },
  { value: '800', text: '800 - Transferência de crédito' },
  { value: '810', text: '810 - Ajuste de IBS na ZFM' },
  { value: '811', text: '811 - Ajustes' },
  { value: '820', text: '820 - Tributação em declaração de regime específico' },
  { value: '830', text: '830 - Exclusão de base de cálculo' },
];

const withCurrent = (entries, currentValue) => {
  if (!currentValue) return entries;
  if (entries.find(item => item.code === String(currentValue))) return entries;
  return [{ code: String(currentValue), text: 'Valor atual' }, ...entries];
};

export const buildCestDatalistOptions = (currentValue) => {
  const descriptionByCode = new Map();
  tabela_cest.forEach(item => {
    const code = String(item.CEST || '').replace(/\D/g, '');
    const description = String(item.Descricao_CEST || '').trim();
    if (code && !descriptionByCode.has(code)) descriptionByCode.set(code, description);
  });
  if (!descriptionByCode.has('0000000')) descriptionByCode.set('0000000', 'CEST genérico');
  const entries = Array.from(descriptionByCode.entries()).map(([code, text]) => ({ code, text }));
  return withCurrent(entries, currentValue);
};

export const buildCfopDatalistOptions = (currentValue) => {
  const descriptionByCode = new Map();
  tabela_cfop.forEach(item => {
    const code = String(item.cfop || '').replace(/\D/g, '');
    const description = String(item.descricao || '').trim();
    if (code && !descriptionByCode.has(code)) descriptionByCode.set(code, description);
  });
  const entries = Array.from(descriptionByCode.entries()).map(([code, text]) => ({ code, text }));
  return withCurrent(entries, currentValue);
};

export const buildCClassTribDatalistOptions = (currentValue) => {
  const entries = (tabela_cclasstrib || [])
    .map(item => ({ code: String(item.code || ''), text: String(item.description || '') }))
    .filter(item => item.code);
  return withCurrent(entries, currentValue);
};

const truncate = (value, max = 70) => {
  const text = String(value || '');
  if (text.length <= max) return text;
  return `${text.slice(0, max)}...`;
};

export const buildSelectOptionsFromDatalist = (entries, maxLabelLength = 70) => ([
  { value: '', text: 'Selecione' },
  ...entries.map(item => ({
    value: item.code,
    text: `${item.code} - ${truncate(item.text, maxLabelLength)}`,
  })),
]);

export const buildLimitedDatalistOptions = (entries, query, {
  minChars = 2,
  limit = 80,
} = {}) => {
  const term = normalize(query);
  if (term.length < minChars) return [];

  const startsWithCode = [];
  const includesCode = [];
  const includesText = [];

  entries.forEach(item => {
    const code = normalize(item.code);
    const text = normalize(item.text);
    if (code.startsWith(term)) {
      startsWithCode.push(item);
      return;
    }
    if (code.includes(term)) {
      includesCode.push(item);
      return;
    }
    if (text.includes(term)) {
      includesText.push(item);
    }
  });

  return [...startsWithCode, ...includesCode, ...includesText].slice(0, limit);
};

export const validateFiscalGroupForm = values => {
  const errors = {};

  if (values.fiscal_data_pRedICMS !== '') {
    const pRedICMS = Number(String(values.fiscal_data_pRedICMS).replace(',', '.'));
    if (!Number.isFinite(pRedICMS) || pRedICMS < 0 || pRedICMS > 100) {
      errors.fiscal_data_pRedICMS = 'Informe um percentual entre 0 e 100';
    }
  }

  if (!values.name) {
    errors.name = 'Este campo é necessário';
  } else if (values.name.length < 3) {
    errors.name = 'O nome precisa ter 3 caracteres ou mais';
  }

  if (!values.fiscal_data_cst) {
    errors.fiscal_data_cst = 'Este campo é necessário';
  } else if (!validCST.includes(values.fiscal_data_cst)) {
    errors.fiscal_data_cst = 'CST inexistente';
  }

  if (!values.fiscal_data_cfop) {
    errors.fiscal_data_cfop = 'Este campo é necessário';
  } else if (!officialCFOPCodes.includes(values.fiscal_data_cfop)) {
    errors.fiscal_data_cfop = 'CFOP inexistente';
  }

  if (values.fiscal_data_cest && !officialCESTCodes.includes(values.fiscal_data_cest) && !genericCEST.includes(values.fiscal_data_cest)) {
    errors.fiscal_data_cest = 'CEST inexistente';
  }

  if (!values.fiscal_data_cClassTrib) {
    errors.fiscal_data_cClassTrib = 'Este campo é necessário';
  } else if (!officialCClassTribCodes.includes(values.fiscal_data_cClassTrib)) {
    errors.fiscal_data_cClassTrib = 'cClassTrib inexistente';
  }

  if (!Object.keys(TRIBUTARY_SITUATION).find(key => TRIBUTARY_SITUATION[key] == values.fiscal_data_tributary_situation)) {
    errors.fiscal_data_tributary_situation = 'Selecione uma opção necessária';
  }

  if (values.fiscal_data_tributary_situation === '1' && !values.fiscal_data_aliq_icms) {
    errors.fiscal_data_aliq_icms = 'Informe a Alíquota do ICMS quando o produto for tributado';
  }

  return errors;
};
